<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import VueMarkdownEditor from '@kangc/v-md-editor';
import enUS from '@kangc/v-md-editor/lib/lang/en-US';
import ruRU from '@/i18n/misc/vue-markdown-editor-ru';
import { useApi } from '@/api';
import { useActivitiesLanguage } from '@/views/CRM/composables/useActivitiesLanguage';
import { useActivitiesMailAction } from '@/views/CRM/composables/useActivitiesMailAction';
import CrmActionParams from '@/views/CRM/components/CrmActionParams.vue';
import CrmErrorPanel from '@/views/CRM/components/CrmErrorPanel.vue';
import UiIframe from '@/views/CRM/components/UiIframe.vue';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import { useLodash } from '@/setup/lodash';
import type { EmitsToListeners } from '@/types/core';
import type { CrmAction, CrmActionGroup, CrmActivity, CrmTemplate } from '@/types/crm';
//#endregion

//#region types
type TemplateId = CrmTemplate['id'];

interface Props {
  activity?: CrmActivity;
  currentBrand?: string;
  templates?: CrmTemplate[];
  group?: CrmActionGroup | null;
  payload?: {
    errors?: {
      email?: Array<Record<string, unknown> | null>;
    };
  };
  templateId?: TemplateId | null;
}

interface Emits {
  save: [actionGroup: CrmActionGroup | null, actions: CrmAction[], actionType: 'email'];
  'change-template': [{ templateId: TemplateId | undefined; cb: (id: TemplateId | undefined) => void }];
  'send-test-email': [{ actionGroup: CrmActionGroup; index: number }];
  close: [];
}

export interface AddMailActionParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  activity: () => ({ action_groups: [] } as unknown as CrmActivity),
  currentBrand: '',
  templates: () => [],
  group: null,
  payload: () => ({}),
  templateId: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const store = useStore();
const { t, locale } = useI18n();
const _ = useLodash();
//#endregion

//#region composables
const editor = ref<any>(null);
const isVisible = ref(false);
const index = ref(-1);
const actionGroup = ref<CrmActionGroup | null>(null);
const actionGroupIndex = ref(-1);
const preview = ref(false);
const message = ref('');
const name = ref('');
const emailSubject = ref('');
const emailTemplateId = ref('');
const errors = ref<Array<Record<string, unknown> | null>>([]);
const previewList = ref<Array<unknown> | null>(null);
const actions = ref<CrmAction[]>([]);

const languagesFromStore = computed(() => store.state.misc?.languages || []);
const {
  languages,
  selectedLanguages,
  defaultLanguageActionIndex,
  handleSelectTab,
  handleMakeDefault,
  setActionsValue,
  debouncedMessageHandlers,
} = useActivitiesLanguage<CrmAction>({
  actions,
  index,
  actionGroupIndex,
  languagesFromStore,
});

const {
  stateEnum,
  currentPreviewState,
  currentPreviewStateWatcherParams,
  isActionsSendDisabled,
  loadPreview,
  loadCurrentPreview,
  handleSendTestEmail: buildSendTestEmailPayload,
  getDefaultForm,
} = useActivitiesMailAction({
  actions,
  index,
  previewList,
  emailTemplateId,
  currentBrand: computed(() => props.currentBrand || ''),
  postEmailTemplatePreview: (payload) => api.postEmailTemplatePreview(payload),
});
//#endregion

//#region computed
const crmTemplateVariables = computed(() => store.getters['crm/crmTemplateVariables'] || []);
const activeTemplate = computed<Partial<CrmTemplate>>(() => (
  props.templates.find(({ id }) => String(id) === String(emailTemplateId.value)) || {}
));
const getPreviewErrors = computed(() => {
  const result: Record<string, unknown> = {};
  if (currentPreviewState.value === stateEnum.error) {
    const markdownError = _.get(previewList.value, [index.value, 'errors', 'markdown', 0, 'message'], null);
    if (markdownError) {
      result.message = markdownError;
    }
  }
  return result;
});
const curentErrors = computed<Record<string, unknown>>(() => (
  errors.value[index.value] || { ...getPreviewErrors.value }
));
const isValid = computed(() => true);
const messageParams = computed(() => crmTemplateVariables.value.map((item: Record<string, string>) => ({
  param: item.name,
  label: item.title,
})));
const mdEditorToolbar = computed(() => {
  const menus = messageParams.value.map((item: { param: string; label: string }) => ({
    name: item.param,
    text: () => item.label,
    action: (mdEditor: any) => mdEditor.insert(() => ({
      text: getParamText(item.param),
    })),
  }));

  return {
    parameter: {
      icon: 'v-md-icon-parameter',
      title: t('crm.activities.actions.action_popup.mail_add_param'),
      hasMenu: true,
      menus,
    },
  };
});
const setPreviewToChangedDebounced = _.debounce((targetIndex: number) => {
  if (previewList.value) {
    previewList.value[targetIndex] = null;
    loadPreview(targetIndex);
  }
}, 1000);
const injectTabIndexDebounced = _.debounce(() => {
  injectTabIndex();
}, 300);
//#endregion

//#region methods
function handleChangeTemplate(templateId: TemplateId | undefined) {
  emit('change-template', {
    templateId,
    cb: (id) => {
      message.value = '';
      actions.value.forEach((_, i) => {
        const target = actions.value[i];
        if (target) {
          target.message = '';
          target.email_template_id = id ? String(id) : '';
        }
      });
      emailTemplateId.value = id ? String(id) : '';
    },
  });
}

function updateActionValue(targetIndex: number) {
  message.value = actions.value[targetIndex]?.message || '';
  name.value = actions.value[targetIndex]?.name || '';
  emailSubject.value = String(actions.value[targetIndex]?.email_subject || '');
}

function open() {
  const targetGroup = props.group;
  const payload = props.payload || {};
  const templateId = props.templateId;

  if (!targetGroup) {
    isVisible.value = true;
    return;
  }

  actionGroup.value = targetGroup;
  actionGroupIndex.value = props.activity.action_groups.indexOf(targetGroup);

  const sourceActions = (props.activity.action_groups[actionGroupIndex.value]?.actions || [])
    .filter((item) => item.type === 'email');

  if (!sourceActions.length) {
    sourceActions.push(getDefaultForm());
  }

  actions.value = _.cloneDeep(sourceActions);

  if (templateId) {
    actions.value.forEach((_, i) => {
      const target = actions.value[i];
      if (target) {
        target.email_template_id = String(templateId);
      }
    });
  }

  emailTemplateId.value = String(templateId || actions.value[0]?.email_template_id || '');
  index.value = 0;
  isVisible.value = true;
  debouncedMessageHandlers.value = {};
  errors.value = [];
  previewList.value = [];

  if (payload?.errors?.email) {
    errors.value = actions.value.map((_, i) => payload.errors?.email?.[i] || null);
  }
}

function close() {
  index.value = -1;
  isVisible.value = false;
  emit('close');
}

function handleSave() {
  emit('save', actionGroup.value, actions.value, 'email');
  close();
}

function handleSendTestEmail() {
  const payload = buildSendTestEmailPayload();
  emit('send-test-email', {
    actionGroup: {
      ...(actionGroup.value || {}),
      actions: payload.actionGroup.actions as CrmAction[],
    } as CrmActionGroup,
    index: payload.index,
  });
}

function getParamText(param: string) {
  return `{{ ${param} }}`;
}

function handleAddParam(param: string, target: 'emailSubject' | 'message') {
  if (target === 'emailSubject') {
    emailSubject.value = `${emailSubject.value || ''}${getParamText(param)}`;
    return;
  }
  message.value = `${message.value || ''}${getParamText(param)}`;
}

function handleAddLanguage(languageCode: string) {
  const newAction = getDefaultForm();
  newAction.language_code = languageCode;
  newAction.is_default = false;
  newAction.email_template_id = emailTemplateId.value;
  actions.value.push(newAction);
}

function handleDelete(targetIndex: number) {
  actions.value.splice(targetIndex, 1);
  errors.value.splice(targetIndex, 1);
  previewList.value?.splice(targetIndex, 1);
  index.value = defaultLanguageActionIndex.value;
  updateActionValue(index.value);
}

function injectTabIndex() {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (_.has({ editor: editor.value }, 'editor.$refs.editorEgine.$refs.textarea')) {
        const textarea = editor.value?.$refs?.editorEgine?.$refs?.textarea;
        if (textarea && +textarea.getAttribute('tabindex') !== 1) {
          textarea.setAttribute('tabindex', '1');
        }
      }
    });
  });
}
//#endregion

//#region watch
watch(locale, async (value) => {
  switch (value) {
    case 'ru':
      VueMarkdownEditor.lang.use('ru-RU', ruRU);
      break;
    case 'en':
    default:
      VueMarkdownEditor.lang.use('en-US', enUS);
  }
  await store.dispatch('crm/getTemplateVariables');
}, { immediate: true });

watch(index, (value) => {
  if (value !== -1) {
    updateActionValue(value);
    if (editor.value?.$refs?.editorEgine) {
      editor.value.$refs.editorEgine.historyStack = [];
    }
  }

  if (currentPreviewState.value !== stateEnum.not_avaliable && previewList.value) {
    previewList.value = previewList.value.map((item) => {
      if (item === stateEnum.error) {
        return null;
      }
      return item;
    });
  }
}, { immediate: true });

watch(currentPreviewStateWatcherParams, (value) => {
  if (value.currentPreviewState === stateEnum.not_loaded) {
    loadCurrentPreview();
  }
}, { immediate: true, deep: true });

watch(emailTemplateId, () => {
  if (previewList.value) {
    previewList.value = [...new Array(previewList.value.length)];
  }
  loadCurrentPreview();
}, { immediate: true });

watch(message, (value) => {
  setActionsValue('message', value);
});

watch(name, (value) => {
  setActionsValue('name', value);
});

watch(emailSubject, (value) => {
  setActionsValue('email_subject', value);
});
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="crm-add-mail-action-popup"
    :title="$t('crm.activities.actions.action_popup.mail_title')"
    :width="1300"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:-my-16 t:-mx-24 t:flex t:items-stretch">
        <div class="crm-add-mail-action-popup__options">
          <div class="t:px-24 t:py-16 t:space-y-16">
            <UiInput
              v-model="name"
              :label="$t('crm.activities.actions.action_popup.email_fields.name')"
              autosize
              size="big"
              :placeholder="$t('crm.activities.actions.action_popup.email_fields.name')"
              type="text"
              :disabled="preview"
              :error="curentErrors.name"
            />
            <CardTitle
              :title="`${$t('crm.activities.actions.mail_template.template')}: ${activeTemplate.name || ''}`"
            >
              <UiAction
                v-if="props.templates.length > 1"
                icon="arrows-rotate"
                :action-name="$t('crm.activities.actions.mail_template.title')"
                color="var(--text-primary)"
                @click="handleChangeTemplate(activeTemplate.id)"
              />
            </CardTitle>
          </div>
          <LanguagesSetup
            :selected-index="index"
            :languages="languages"
            :selected-languages="selectedLanguages"
            :errors="errors"
            @add-language="handleAddLanguage"
            @select-index="handleSelectTab"
            @delete-language="handleDelete(index)"
            @make-default="handleMakeDefault(index)"
          >
            <div class="t:space-y-16">
              <div class="t:space-y-4">
                <UiInput
                  v-model="emailSubject"
                  :label="$t('crm.activities.actions.action_popup.email_fields.subject')"
                  autosize
                  size="big"
                  :placeholder="$t('crm.activities.actions.action_popup.email_fields.subject')"
                  type="text"
                  :disabled="preview"
                />
                <CrmActionParams
                  :disabled="preview"
                  :message-params="messageParams"
                  @select="handleAddParam($event, 'emailSubject')"
                />
              </div>
              <div class="t:space-y-4">
                <div
                  :class="[
                    'crm-add-mail-action-popup__message-editor',
                    {'crm-add-mail-action-popup__message-editor_error': curentErrors.message}
                  ]"
                >
                  <!-- eslint-disable-next-line vue/component-name-in-template-casing -->
                  <v-md-editor
                    ref="editor"
                    v-model="message"
                    mode="edit"
                    right-toolbar="fullscreen"
                    left-toolbar="undo redo clear | h bold italic | ul ol | link image | parameter"
                    :toolbar="mdEditorToolbar"
                    height="340px"
                    tabindex="1"
                    @update:model-value="setPreviewToChangedDebounced(index)"
                    @change="injectTabIndexDebounced"
                  />
                </div>
                <CrmErrorPanel
                  v-if="curentErrors.message"
                >
                  {{ curentErrors.message }}
                </CrmErrorPanel>
              </div>
            </div>
          </LanguagesSetup>
        </div>
        <div class="t:w-1/2 t:flex t:justify-center">
          <UiIframe
            v-if="currentPreviewState === stateEnum.loaded"
            :html="String(previewList?.[index] || '')"
          />
          <div
            v-else
            v-loading="true"
          />
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          icon="paper-plane"
          :disabled="isActionsSendDisabled"
          @click="handleSendTestEmail()"
        >
          {{ $t('crm.buttons.send_test_email') }}
        </UiButton>
        <UiButton
          :disabled="!isValid"
          filled
          icon="check"
          @click="handleSave()"
        >
          {{ $t('crm.buttons.save_action') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.crm-add-mail-action-popup {
  &__options {
    width: 50%;
    border-right: 1px solid var(--border-default);
  }

  &__message-editor {
    &.v-md-editor--fullscreen {
      width: auto;
    }
    .v-md-icon-parameter::before {
      content: '\e707';
    }
    .v-md-icon-fullscreen .v-md-editor__tooltip {
      left: auto !important;
      right: 0;
    }
    .v-md-icon-sync .v-md-editor__tooltip {
      left: auto !important;
      right: 0;
    }
    &_error {
      border: 1px solid var(--text-negative);
    }
    .v-md-editor__toolbar-left + .v-md-editor__toolbar-right {
      margin-left: 10px;
    }
  }
}
</style>

